import { create } from "zustand";
import { toast } from "sonner";
import type { DuplicatePhotoInfo, PhotoUploadResult } from "../types/api";

interface LotUploadResult {
  created: Array<{ lotNumber: number; assetId: number; photoCount: number }>;
  attached: Array<{ lotNumber: number; assetId: number; photoCount: number }>;
  unmatched: Array<{ filename: string }>;
  duplicates?: DuplicatePhotoInfo[];
}

interface UploadProgressState {
  total: number;
  completed: number;
  failed: number;
  active: boolean;
  assetsQueued: number;
  hashing: { total: number; completed: number } | null;
}

interface UploadState {
  upload: UploadProgressState;
  pendingDuplicates: DuplicatePhotoInfo[];
  uploadFiles: (files: File[]) => void;
  uploadFilesToAsset: (assetId: number, files: File[]) => void;
  clearAssetsQueued: () => void;
  clearDuplicates: () => void;
}

async function hashFile(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hash = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function checkDuplicates(
  files: File[],
  onHashProgress?: (completed: number) => void
): Promise<{ newFiles: File[]; duplicates: DuplicatePhotoInfo[] }> {
  const token = localStorage.getItem("auth_token");
  const fileHashes: { file: File; hash: string }[] = [];
  for (const file of files) {
    fileHashes.push({ file, hash: await hashFile(file) });
    onHashProgress?.(fileHashes.length);
  }

  const hashes = fileHashes.map((fh) => fh.hash);

  try {
    const res = await fetch("/api/assets/check-duplicates", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ hashes }),
    });

    if (!res.ok) return { newFiles: files, duplicates: [] };

    const { duplicates: dupMap } = (await res.json()) as {
      duplicates: Record<string, { assetId: number; photoUrl: string }>;
    };

    const newFiles: File[] = [];
    const duplicates: DuplicatePhotoInfo[] = [];

    for (const { file, hash } of fileHashes) {
      const hit = dupMap[hash];
      if (hit) {
        duplicates.push({
          uploadedFilename: file.name,
          existingPhotoUrl: hit.photoUrl,
          existingAssetId: hit.assetId,
          existingAssetNickname: `Asset ${hit.assetId}`,
        });
      } else {
        newFiles.push(file);
      }
    }

    return { newFiles, duplicates };
  } catch {
    return { newFiles: files, duplicates: [] };
  }
}

export const useUploadStore = create<UploadState>()((set, get) => ({
  upload: {
    total: 0,
    completed: 0,
    failed: 0,
    active: false,
    assetsQueued: 0,
    hashing: null,
  },
  pendingDuplicates: [],

  clearAssetsQueued: () =>
    set((s) => ({ upload: { ...s.upload, assetsQueued: 0 } })),

  clearDuplicates: () => set({ pendingDuplicates: [] }),

  uploadFiles: (files) => {
    const imageFiles = files.filter((f) => f.type.startsWith("image/"));
    if (imageFiles.length === 0) {
      toast.error("No image files detected");
      return;
    }

    set((s) => ({
      upload: {
        total: imageFiles.length,
        completed: 0,
        failed: 0,
        active: true,
        assetsQueued: s.upload.assetsQueued,
        hashing: { total: imageFiles.length, completed: 0 },
      },
    }));

    checkDuplicates(imageFiles, (completed) => {
      set((s) => ({
        upload: { ...s.upload, hashing: { total: imageFiles.length, completed } },
      }));
    }).then(({ newFiles, duplicates: clientDuplicates }) => {
      set((s) => ({ upload: { ...s.upload, hashing: null } }));
      if (clientDuplicates.length > 0) {
        set((s) => ({
          pendingDuplicates: [...s.pendingDuplicates, ...clientDuplicates],
        }));
      }

      if (newFiles.length === 0) {
        set((s) => ({
          upload: { ...s.upload, completed: imageFiles.length, active: false },
        }));
        if (clientDuplicates.length > 0) {
          toast.info(
            `All ${clientDuplicates.length} photo${clientDuplicates.length > 1 ? "s" : ""} already exist`
          );
        }
        return;
      }

      const formData = new FormData();
      const batchId = crypto.randomUUID();
      formData.append("batchId", batchId);
      const fileSizes: number[] = [];
      newFiles.forEach((file) => {
        formData.append("images", file);
        fileSizes.push(file.size);
      });

      const cumulativeSizes = fileSizes.map((_, i) =>
        fileSizes.slice(0, i + 1).reduce((sum, s) => sum + s, 0)
      );

      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        let estimatedCompleted = clientDuplicates.length;
        for (let i = 0; i < cumulativeSizes.length; i++) {
          if (e.loaded >= (cumulativeSizes[i] ?? 0)) estimatedCompleted++;
          else break;
        }
        set((s) => ({ upload: { ...s.upload, completed: estimatedCompleted } }));
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          let result: LotUploadResult | null = null;
          try {
            result = JSON.parse(xhr.responseText);
          } catch {}

          const grouped = result ? result.created.length + result.attached.length : 0;
          const unmatchedCount = result?.unmatched.length ?? 0;

          set((s) => ({
            upload: {
              ...s.upload,
              completed: imageFiles.length,
              active: true,
              assetsQueued: s.upload.assetsQueued + grouped,
            },
          }));

          if (grouped > 0) {
            toast.success(
              `${grouped} lot${grouped > 1 ? "s" : ""} matched, ${newFiles.length} image${newFiles.length > 1 ? "s" : ""} uploaded`
            );
          }
          if (unmatchedCount > 0) {
            toast.warning(
              `${unmatchedCount} image${unmatchedCount > 1 ? "s" : ""} could not be matched to a lot`
            );
          }
          if (result?.duplicates && result.duplicates.length > 0) {
            set((s) => ({
              pendingDuplicates: [...s.pendingDuplicates, ...result!.duplicates!],
            }));
          }

          setTimeout(() => {
            set((s) => ({
              upload: { ...s.upload, total: 0, completed: 0, failed: 0, active: false },
            }));
          }, 2000);
        } else {
          set((s) => ({
            upload: {
              ...s.upload,
              failed: imageFiles.length - s.upload.completed,
              active: false,
            },
          }));
          toast.error(`Upload failed: ${xhr.statusText}`);
        }
      };

      xhr.onerror = () => {
        set((s) => ({
          upload: {
            ...s.upload,
            failed: imageFiles.length - s.upload.completed,
            active: false,
          },
        }));
        toast.error("Upload failed: Network error");
      };

      xhr.open("POST", "/api/queue/upload-by-lot");
      const token = localStorage.getItem("auth_token");
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.send(formData);
    });
  },

  uploadFilesToAsset: (assetId, files) => {
    const imageFiles = files.filter((f) => f.type.startsWith("image/"));
    if (imageFiles.length === 0) {
      toast.error("No image files detected");
      return;
    }

    set((s) => ({
      upload: {
        total: imageFiles.length,
        completed: 0,
        failed: 0,
        active: true,
        assetsQueued: s.upload.assetsQueued,
        hashing: { total: imageFiles.length, completed: 0 },
      },
    }));

    checkDuplicates(imageFiles, (completed) => {
      set((s) => ({
        upload: { ...s.upload, hashing: { total: imageFiles.length, completed } },
      }));
    }).then(({ newFiles, duplicates: clientDuplicates }) => {
      set((s) => ({ upload: { ...s.upload, hashing: null } }));
      if (clientDuplicates.length > 0) {
        set((s) => ({
          pendingDuplicates: [...s.pendingDuplicates, ...clientDuplicates],
        }));
      }

      if (newFiles.length === 0) {
        set((s) => ({
          upload: { ...s.upload, completed: imageFiles.length, active: false },
        }));
        if (clientDuplicates.length > 0) {
          toast.info(
            `All ${clientDuplicates.length} photo${clientDuplicates.length > 1 ? "s" : ""} already exist`
          );
        }
        return;
      }

      const formData = new FormData();
      newFiles.forEach((file) => formData.append("images", file));
      const fileSizes = newFiles.map((f) => f.size);
      const cumulativeSizes = fileSizes.map((_, i) =>
        fileSizes.slice(0, i + 1).reduce((sum, s) => sum + s, 0)
      );

      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        let estimatedCompleted = clientDuplicates.length;
        for (let i = 0; i < cumulativeSizes.length; i++) {
          if (e.loaded >= (cumulativeSizes[i] ?? 0)) estimatedCompleted++;
          else break;
        }
        set((s) => ({ upload: { ...s.upload, completed: estimatedCompleted } }));
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          let result: PhotoUploadResult | null = null;
          try {
            result = JSON.parse(xhr.responseText);
          } catch {}

          const addedCount = result?.photos.length ?? newFiles.length;
          set((s) => ({
            upload: { ...s.upload, completed: imageFiles.length, active: true },
          }));

          if (addedCount > 0) {
            toast.success(
              `${addedCount} photo${addedCount > 1 ? "s" : ""} added to asset`
            );
          }
          if (result?.duplicates && result.duplicates.length > 0) {
            set((s) => ({
              pendingDuplicates: [...s.pendingDuplicates, ...result!.duplicates!],
            }));
          }

          setTimeout(() => {
            set((s) => ({
              upload: { ...s.upload, total: 0, completed: 0, failed: 0, active: false },
            }));
          }, 2000);
        } else {
          set((s) => ({
            upload: {
              ...s.upload,
              failed: imageFiles.length - s.upload.completed,
              active: false,
            },
          }));
          toast.error(`Upload failed: ${xhr.statusText}`);
        }
      };

      xhr.onerror = () => {
        set((s) => ({
          upload: {
            ...s.upload,
            failed: imageFiles.length - s.upload.completed,
            active: false,
          },
        }));
        toast.error("Upload failed: Network error");
      };

      xhr.open("POST", `/api/assets/${assetId}/upload-photos`);
      const token = localStorage.getItem("auth_token");
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.send(formData);
    });
  },
}));
